import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { providersAPI, bookingsAPI, adminSettingsAPI, schedulesAPI } from '../services/api';
import { Provider } from '../types';
import { ArrowLeft, Calendar, Moon, Loader, AlertCircle, ChevronRight, ChevronLeft, CheckCircle, Clock, MapPin } from 'lucide-react';
import LocationSearchInputBackend from '../components/LocationSearchInputBackend';
import MapPicker from '../components/MapPicker';
import CombinedLocationSelector from '../components/CombinedLocationSelector';
import ReviewDisplay from '../components/ReviewDisplay';

// Define steps
const STEPS = {
  PICKUP: 1,
  DROPOFF: 2,
  BOOKING_DETAILS: 3,
};

interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
}

const BookDriverPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(STEPS.PICKUP);
  
  // Location states
  const [pickupSearchText, setPickupSearchText] = useState('');
  const [dropoffSearchText, setDropoffSearchText] = useState('');
  const [pickupLocation, setPickupLocation] = useState<LocationData | null>(null);
  const [dropoffLocation, setDropoffLocation] = useState<LocationData | null>(null);
  const [tempPickupLocation, setTempPickupLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [tempDropoffLocation, setTempDropoffLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Booking details
  const [numberOfNights, setNumberOfNights] = useState(0);
  // Calculate distance between two points using Haversine formula
  const calculateDistanceBetweenPoints = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  // Initialize with current date and time
  const getCurrentDateTime = () => {
    const now = new Date();
    // Round to nearest 15 minutes
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    // Format as datetime-local string (YYYY-MM-DDTHH:mm)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return {
      datetime: `${year}-${month}-${day}T${hours}:${mins}`,
      date: `${year}-${month}-${day}`,
      time: `${hours}:${mins}`
    };
  };
  
  const initialDateTime = getCurrentDateTime();
  const [bookingDate, setBookingDate] = useState(initialDateTime.date);
  const [startTime, setStartTime] = useState(initialDateTime.time);
  const [endTime, setEndTime] = useState('10:00');
  const [startDateTime, setStartDateTime] = useState(initialDateTime.datetime);
  const [endDateTime, setEndDateTime] = useState('');
  const [minEndDateTime, setMinEndDateTime] = useState('');
  const [notes, setNotes] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState<any>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [oneWayDistance, setOneWayDistance] = useState<number | null>(null); // Pickup-to-dropoff distance for threshold check
  const [oneWayDurationSeconds, setOneWayDurationSeconds] = useState<number>(0); // Store one-way duration for end time calculation
  const [bookedTimes, setBookedTimes] = useState<Array<{ startTime: string; endTime: string }>>([]);
  const [hasSchedule, setHasSchedule] = useState<boolean>(false);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availableSlots, setAvailableSlots] = useState<Array<{ startTime: string; endTime: string }>>([]);
  const [agreedToTnC, setAgreedToTnC] = useState(false);
  const [tripType, setTripType] = useState<'roundTrip' | 'oneWay'>('roundTrip');
  const [oneWayAllowed, setOneWayAllowed] = useState(false);
  const [twoWayTripDistanceLimit, setTwoWayTripDistanceLimit] = useState<number | null>(null);
  const [intermediateDestinations, setIntermediateDestinations] = useState<Array<{
    latitude: number;
    longitude: number;
    address: string;
    order: number;
  }>>([]);
  const [maxDestinations, setMaxDestinations] = useState(5);
  const [adminSettings, setAdminSettings] = useState<{
    driverPricing?: {
      platformFeePercentage?: number;
      taxPercentage?: number;
    };
  } | null>(null);

  // Recent locations
  const [recentLocations, setRecentLocations] = useState<Array<{
    pickup: LocationData;
    dropoff: LocationData;
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
    timestamp: number;
  }>>([]);
  const [hasManualSearch, setHasManualSearch] = useState(false);

  useEffect(() => {
    fetchProvider();
    loadRecentLocations();
    fetchAdminSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAdminSettings = async () => {
    try {
      const response = await adminSettingsAPI.getSettings();
      if (response.success && response.data) {
        if (response.data.maxDestinations !== undefined) {
          setMaxDestinations(response.data.maxDestinations);
        }
        setOneWayAllowed(!!response.data.oneWayTripThreshold);
        setTwoWayTripDistanceLimit(response.data.twoWayTripDistanceLimit ?? null);
        setAdminSettings({
          driverPricing: response.data.driverPricing
        });
      }
    } catch (error) {
      console.error('Error fetching admin settings:', error);
    }
  };

  // Calculate distance when both locations are set
  useEffect(() => {
    if (pickupLocation && dropoffLocation) {
      calculateDistance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLocation, dropoffLocation]);

  // Calculate number of nights from startDateTime and endDateTime
  const calculateNumberOfNights = (start: string, end: string): number => {
    if (!start || !end) return 0;
    
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      // Get date portions (YYYY-MM-DD) by setting time to midnight
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);
      
      // Calculate difference in days
      const diffTime = endDateOnly.getTime() - startDateOnly.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // If end date is on a later day, that's the number of nights
      // e.g., Start: Day 1, End: Day 2 = 1 night
      // e.g., Start: Day 1, End: Day 3 = 2 nights
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('Error calculating nights:', error);
      return 0;
    }
  };

  // Update end datetime when start datetime or distance changes
  useEffect(() => {
    if (startDateTime && distance !== null && distance > 0) {
      // Estimate duration based on distance (assume 40 km/h average)
      const estimatedDurationSeconds = (distance / 40) * 3600;
      const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
      setMinEndDateTime(minEnd);
      // Always update end datetime to minimum if it's not set or is before minimum
      if (!endDateTime || endDateTime < minEnd) {
        setEndDateTime(minEnd);
        // Also update endTime for backward compatibility
        const timeOnly = minEnd.split('T')[1];
        setEndTime(timeOnly);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, distance]);

  // Auto-calculate numberOfNights when startDateTime or endDateTime changes
  useEffect(() => {
    if (startDateTime && endDateTime) {
      const nights = calculateNumberOfNights(startDateTime, endDateTime);
      setNumberOfNights(nights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime]);

  // Recalculate distance and end time when trip type changes
  useEffect(() => {
    if (pickupLocation && dropoffLocation && oneWayDistance !== null) {
      recalculateForTripType(tripType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripType]);

  // Calculate price when distance, nights, intermediate destinations, or trip type changes
  useEffect(() => {
    if ((distance !== null || intermediateDestinations.length > 0) && numberOfNights >= 0) {
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distance, numberOfNights, intermediateDestinations, tripType]);

  // Auto-set to one-way if distance exceeds two-way limit
  useEffect(() => {
    if (distance !== null && 
        twoWayTripDistanceLimit !== null && 
        distance > twoWayTripDistanceLimit && 
        intermediateDestinations.length === 0) {
      setTripType('oneWay');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distance, twoWayTripDistanceLimit, intermediateDestinations.length]);

  // Fetch driver's booked times when booking date changes
  useEffect(() => {
    if (bookingDate && provider) {
      fetchBookedTimes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDate, provider]);

  // Initialize dropoff location fields when navigating to step 2 with existing dropoff location
  useEffect(() => {
    if (currentStep === STEPS.DROPOFF && dropoffLocation && !tempDropoffLocation) {
      setTempDropoffLocation({
        lat: dropoffLocation.latitude,
        lng: dropoffLocation.longitude
      });
      if (!dropoffSearchText) {
        setDropoffSearchText(dropoffLocation.address);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, dropoffLocation]);

  const fetchProvider = async () => {
    try {
      setLoading(true);
      const response = await providersAPI.getProviderById(id!);
      setProvider(response.data.provider);
    } catch (error) {
      console.error('Error fetching provider:', error);
      setError('Driver not found');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentLocations = async () => {
    try {
      const bookingsResponse = await bookingsAPI.getMyBookings({ limit: 100 });
      const bookings = bookingsResponse?.data || [];
      
      if (!Array.isArray(bookings)) {
        setRecentLocations([]);
        return;
      }
      
      // Filter driver bookings
      const driverBookings = bookings.filter((booking: any) => 
        booking?.serviceType === 'driver' && 
        booking?.pickupLocation && 
        booking?.dropoffLocation &&
        booking.pickupLocation.latitude != null &&
        booking.pickupLocation.longitude != null &&
        booking.dropoffLocation.latitude != null &&
        booking.dropoffLocation.longitude != null
      );

      if (driverBookings.length === 0) {
        setRecentLocations([]);
        return;
      }

      // Sort by createdAt descending
      const sorted = driverBookings.sort((a: any, b: any) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      // Get the 2 most recent bookings
      const recentBookings = sorted.slice(0, 2);
      if (recentBookings.length === 0) {
        setRecentLocations([]);
        return;
      }

      // Convert to recent location format
      const recentLocations = recentBookings
        .filter((booking: any) => 
          booking?.pickupLocation && 
          booking?.dropoffLocation &&
          booking.pickupLocation.latitude != null &&
          booking.pickupLocation.longitude != null &&
          booking.dropoffLocation.latitude != null &&
          booking.dropoffLocation.longitude != null
        )
        .map((booking: any) => {
          // Process intermediate destinations
          let intermediateDestinations: Array<{
            latitude: number;
            longitude: number;
            address: string;
            order: number;
          }> | undefined = undefined;
          
          if (booking.intermediateDestinations && Array.isArray(booking.intermediateDestinations)) {
            const validStops = booking.intermediateDestinations
              .filter((stop: any) => 
                stop && 
                typeof stop.latitude === 'number' && 
                typeof stop.longitude === 'number' &&
                !isNaN(stop.latitude) &&
                !isNaN(stop.longitude)
              )
              .map((stop: any, index: number) => ({
                latitude: stop.latitude,
                longitude: stop.longitude,
                address: stop.address || `Intermediate stop ${index + 1}`,
                order: typeof stop.order === 'number' ? stop.order : index + 1
              }));
            
            if (validStops.length > 0) {
              intermediateDestinations = validStops.sort((a: { order: number }, b: { order: number }) => a.order - b.order);
            }
          }
          
          return {
            pickup: {
              latitude: booking.pickupLocation.latitude,
              longitude: booking.pickupLocation.longitude,
              address: booking.pickupLocation.address || 'Pickup location'
            },
            dropoff: {
              latitude: booking.dropoffLocation.latitude,
              longitude: booking.dropoffLocation.longitude,
              address: booking.dropoffLocation.address || 'Dropoff location'
            },
            intermediateDestinations,
            timestamp: booking?.createdAt ? new Date(booking.createdAt).getTime() : Date.now()
          };
        });

      setRecentLocations(recentLocations);
    } catch (error) {
      console.error('Error loading recent locations:', error);
      setRecentLocations([]);
    }
  };

  const selectRecentLocation = (location: { 
    pickup: LocationData; 
    dropoff: LocationData; 
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
  }) => {
    setPickupLocation(location.pickup);
    setDropoffLocation(location.dropoff);
    setPickupSearchText(location.pickup.address);
    setDropoffSearchText(location.dropoff.address);
    // Set intermediate destinations if they exist
    if (location.intermediateDestinations && location.intermediateDestinations.length > 0) {
      setIntermediateDestinations(location.intermediateDestinations);
    } else {
      setIntermediateDestinations([]);
    }
    // Skip directly to booking details
    setCurrentStep(STEPS.BOOKING_DETAILS);
  };

  const fetchBookedTimes = async () => {
    try {
      if (!provider || !bookingDate) return;
      
      const response = await schedulesAPI.getDriverBookedTimes(
        provider._id || provider.id!,
        bookingDate
      );
      
      setHasSchedule(response.data.hasSchedule);
      setIsAvailable(response.data.isAvailable);
      setBookedTimes(response.data.bookedTimes || []);
      setAvailableSlots(response.data.availableSlots || []);
      
      console.log('📅 Driver availability fetched:', {
        hasSchedule: response.data.hasSchedule,
        isAvailable: response.data.isAvailable,
        bookedCount: response.data.bookedTimes?.length ?? 0,
        availableCount: response.data.availableSlots?.length ?? 0
      });
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Not a critical error, just set defaults
      setHasSchedule(false);
      setIsAvailable(false);
      setBookedTimes([]);
      setAvailableSlots([]);
    }
  };

  // Calculate available time ranges by subtracting booked times from full day
  const calculateAvailableRanges = (bookedTimes: Array<{ startTime: string; endTime: string }>): Array<{ startTime: string; endTime: string }> => {
    if (bookedTimes.length === 0) {
      return [{ startTime: '00:00', endTime: '23:59' }];
    }

    // Sort booked times by start time
    const sorted = [...bookedTimes].sort((a, b) => a.startTime.localeCompare(b.startTime));
    
    const available: Array<{ startTime: string; endTime: string }> = [];
    
    // Check if there's time before first booking
    if (sorted[0].startTime !== '00:00') {
      available.push({
        startTime: '00:00',
        endTime: sorted[0].startTime
      });
    }
    
    // Check gaps between bookings
    for (let i = 0; i < sorted.length - 1; i++) {
      const currentEnd = sorted[i].endTime;
      const nextStart = sorted[i + 1].startTime;
      
      if (currentEnd !== nextStart) {
        available.push({
          startTime: currentEnd,
          endTime: nextStart
        });
      }
    }
    
    // Check if there's time after last booking
    const lastBooking = sorted[sorted.length - 1];
    if (lastBooking.endTime !== '23:59') {
      available.push({
        startTime: lastBooking.endTime,
        endTime: '23:59'
      });
    }
    
    return available;
  };

  const calculateDistance = () => {
    if (!pickupLocation || !dropoffLocation) return;

    // Use Google Maps Distance Matrix API
    const service = new google.maps.DistanceMatrixService();
    
    service.getDistanceMatrix(
      {
        origins: [{ lat: pickupLocation.latitude, lng: pickupLocation.longitude }],
        destinations: [{ lat: dropoffLocation.latitude, lng: dropoffLocation.longitude }],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === 'OK' && response) {
          const distanceInMeters = response.rows[0].elements[0].distance?.value ?? 0;
          const distanceInKm = distanceInMeters / 1000;
          const durationInSeconds = response.rows[0].elements[0].duration?.value ?? 0;
          
          setDistance(distanceInKm);
          setOneWayDistance(distanceInKm); // Store pickup-to-dropoff distance for threshold check
          setOneWayDurationSeconds(durationInSeconds); // Store one-way duration
          console.log('📏 Distance calculated:', distanceInKm, 'km');
          console.log('⏱️ Duration:', durationInSeconds, 'seconds');
          
          // Calculate minimum end datetime based on duration + 30 mins buffer
          if (startDateTime && durationInSeconds) {
            const minEnd = calculateMinEndDateTime(startDateTime, durationInSeconds);
            setMinEndDateTime(minEnd);
            if (!endDateTime || endDateTime < minEnd) {
              setEndDateTime(minEnd);
              // Also update endTime for backward compatibility
              const timeOnly = minEnd.split('T')[1];
              setEndTime(timeOnly);
            }
          } else if (startDateTime) {
            // Fallback: estimate from distance if duration not available
            const estimatedDurationSeconds = (distanceInKm / 40) * 3600;
            const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
            setMinEndDateTime(minEnd);
            if (!endDateTime || endDateTime < minEnd) {
              setEndDateTime(minEnd);
              // Also update endTime for backward compatibility
              const timeOnly = minEnd.split('T')[1];
              setEndTime(timeOnly);
            }
          }
        } else {
          console.error('Distance calculation failed:', status);
          // Fallback to Haversine formula
          const dist = haversineDistance(
            pickupLocation.latitude,
            pickupLocation.longitude,
            dropoffLocation.latitude,
            dropoffLocation.longitude
          );
          setDistance(dist);
          setOneWayDistance(dist); // Store pickup-to-dropoff distance for threshold check
          
          // Estimate duration for fallback (assume 40 km/h average)
          if (startDateTime && dist) {
            const estimatedDurationSeconds = (dist / 40) * 3600; // Convert to seconds
            setOneWayDurationSeconds(estimatedDurationSeconds); // Store one-way duration
            const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
            setMinEndDateTime(minEnd);
            if (!endDateTime || endDateTime < minEnd) {
              setEndDateTime(minEnd);
              // Also update endTime for backward compatibility
              const timeOnly = minEnd.split('T')[1];
              setEndTime(timeOnly);
            }
          }
        }
      }
    );
  };

  // Calculate minimum end datetime based on start datetime and travel duration
  const calculateMinEndDateTime = (startDT: string, durationSeconds: number): string => {
    const startDate = new Date(startDT);
    const durationMinutes = Math.ceil(durationSeconds / 60) + 30; // Add 30 min buffer
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    
    // Format as datetime-local string (YYYY-MM-DDTHH:mm)
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Calculate suggested end time based on start time and duration
  const calculateSuggestedEndTime = (start: string, durationSeconds: number): string => {
    const durationMinutes = Math.ceil(durationSeconds / 60) + 30; // Add 30 min buffer
    return addMinutesToTime(start, durationMinutes);
  };

  // Add minutes to a time string (HH:MM)
  const addMinutesToTime = (time: string, minutes: number): string => {
    const [hours, mins] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, mins, 0, 0);
    date.setMinutes(date.getMinutes() + minutes);
    
    const newHours = date.getHours().toString().padStart(2, '0');
    const newMins = date.getMinutes().toString().padStart(2, '0');
    return `${newHours}:${newMins}`;
  };

  // Update minimum end datetime when start datetime or distance changes
  useEffect(() => {
    if (distance && startDateTime) {
      // Estimate duration based on distance (assume 40 km/h average)
      const estimatedDurationSeconds = (distance / 40) * 3600;
      const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
      setMinEndDateTime(minEnd);
      if (!endDateTime || endDateTime < minEnd) {
        setEndDateTime(minEnd);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, distance]);

  // Haversine formula fallback
  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Recalculate distance display and end date/time when trip type changes
  const recalculateForTripType = async (newTripType: 'roundTrip' | 'oneWay') => {
    if (!pickupLocation || !dropoffLocation) return;

    // Ensure we have oneWayDistance - calculate if not available
    let currentOneWayDistance = oneWayDistance;
    if (!currentOneWayDistance) {
      currentOneWayDistance = haversineDistance(
        pickupLocation.latitude,
        pickupLocation.longitude,
        dropoffLocation.latitude,
        dropoffLocation.longitude
      );
      setOneWayDistance(currentOneWayDistance);
    }

    // Calculate displayed distance based on trip type
    let displayDistance = currentOneWayDistance;
    if (newTripType === 'roundTrip' && !intermediateDestinations.length) {
      // For round trip without intermediate destinations, add return distance
      const returnDistance = calculateDistanceBetweenPoints(
        dropoffLocation.latitude,
        dropoffLocation.longitude,
        pickupLocation.latitude,
        pickupLocation.longitude
      );
      displayDistance = currentOneWayDistance + returnDistance;
    }
    // For one-way or multi-destination trips, use one-way distance
    setDistance(displayDistance);

    // Recalculate end date/time based on trip type
    const currentDuration = oneWayDurationSeconds > 0 ? oneWayDurationSeconds : null;
    if (startDateTime) {
      let totalDurationSeconds: number;
      
      if (currentDuration) {
        // Use stored duration
        totalDurationSeconds = newTripType === 'roundTrip' && !intermediateDestinations.length
          ? currentDuration * 2
          : currentDuration;
      } else if (currentOneWayDistance) {
        // Fallback: estimate from distance
        const avgSpeed = 40; // km/h
        totalDurationSeconds = newTripType === 'roundTrip' && !intermediateDestinations.length
          ? (displayDistance / avgSpeed) * 3600
          : (currentOneWayDistance / avgSpeed) * 3600;
      } else {
        return; // Can't calculate without distance
      }
      
      const minEnd = calculateMinEndDateTime(startDateTime, totalDurationSeconds);
      setMinEndDateTime(minEnd);
      if (!endDateTime || endDateTime < minEnd) {
        setEndDateTime(minEnd);
        const timeOnly = minEnd.split('T')[1];
        setEndTime(timeOnly);
      }
    }
  };

  const calculatePrice = async (overrideTripType?: 'roundTrip' | 'oneWay') => {
    if (distance === null && intermediateDestinations.length === 0) return;

    try {
      // Use overrideTripType if provided, otherwise use current tripType state
      const currentTripType = overrideTripType ?? tripType;
      
      const settingsResponse = await adminSettingsAPI.getSettings();
      const threshold = settingsResponse.data?.oneWayTripThreshold;
      
      // Calculate pickup-to-dropoff distance for threshold check
      // If oneWayDistance is not set, calculate it now
      let distanceForThreshold = oneWayDistance;
      if (distanceForThreshold === null && pickupLocation && dropoffLocation) {
        // Calculate pickup-to-dropoff distance using Haversine formula
        distanceForThreshold = haversineDistance(
          pickupLocation.latitude,
          pickupLocation.longitude,
          dropoffLocation.latitude,
          dropoffLocation.longitude
        );
        setOneWayDistance(distanceForThreshold); // Store it for future use
      }
      
      // Use oneWayDistance (pickup-to-dropoff) for threshold check, never use total distance
      // If still null, fall back to 0 (shouldn't happen if pickup and dropoff are set)
      const finalDistanceForThreshold = distanceForThreshold ?? 0;
      const allowed = threshold !== undefined && threshold !== null && finalDistanceForThreshold < threshold;
      setOneWayAllowed(allowed);

      // If multi-destination, use calculateMultiDestinationPrice
      if (intermediateDestinations.length > 0 && pickupLocation && dropoffLocation) {
        // Use current tripType (with override if provided)
        // For roundTrip, always use roundTrip
        // For oneWay, only use it if allowed, otherwise default to roundTrip
        const effectiveTripType = currentTripType === 'roundTrip' ? 'roundTrip' : (oneWayAllowed ? 'oneWay' : 'roundTrip');
        const response = await adminSettingsAPI.calculateMultiDestinationPrice({
          origin: { latitude: pickupLocation.latitude, longitude: pickupLocation.longitude },
          intermediateDestinations: intermediateDestinations.map(s => ({
            latitude: s.latitude,
            longitude: s.longitude
          })),
          finalDestination: { latitude: dropoffLocation.latitude, longitude: dropoffLocation.longitude },
          numberOfNights,
          isTwoWay: effectiveTripType === 'roundTrip' // Round trip: A->B->C->A, One-way: A->B->C
        });
        setEstimatedPrice(response.data);
        if (response.data.distance !== undefined && response.data.distance !== null) {
          setDistance(response.data.distance);
        }
        console.log('💰 Multi-destination price calculated:', response.data);
      } else {
        // Single destination - calculate based on trip type
        // For roundTrip, always use roundTrip
        // For oneWay, only use it if allowed, otherwise default to roundTrip
        const effectiveTripType = currentTripType === 'roundTrip' ? 'roundTrip' : (allowed ? 'oneWay' : 'roundTrip');
        
        // Calculate distance for pricing based on trip type
        // For one-way: use only one-way distance (pickup-to-dropoff)
        // For round trip: calculate round trip distance (one-way + return)
        let distanceForPricing: number;
        if (effectiveTripType === 'oneWay') {
          // One-way: use only the one-way distance
          distanceForPricing = oneWayDistance ?? distance ?? 0;
        } else {
          // Round trip: calculate total distance (one-way + return)
          if (!pickupLocation || !dropoffLocation) {
            console.error('calculatePrice - Missing pickup or dropoff location for round trip calculation');
            return;
          }
          const oneWayDist = oneWayDistance ?? distance ?? 0;
          const returnDist = calculateDistanceBetweenPoints(
            dropoffLocation.latitude,
            dropoffLocation.longitude,
            pickupLocation.latitude,
            pickupLocation.longitude
          );
          distanceForPricing = oneWayDist + returnDist;
          // Update distance state to reflect round trip distance
          setDistance(distanceForPricing);
        }
        
        console.log('calculatePrice - Calculating price for distance:', distanceForPricing, 'km, tripType:', effectiveTripType);
        const response = await adminSettingsAPI.calculateDriverPrice({
          distance: distanceForPricing,
          numberOfNights,
          tripType: effectiveTripType
        });
        setEstimatedPrice(response.data);
        // Update distance from response if available
        if (response.data.distance !== undefined && response.data.distance !== null) {
          setDistance(response.data.distance);
        }
        console.log('💰 Price calculated:', response.data);
      }
    } catch (error: any) {
      console.error('Error calculating price:', error);
    }
  };

  const handlePickupPlaceSelected = (value: string, placeDetails?: any) => {
    setHasManualSearch(true);
    if (placeDetails?.location) {
      setTempPickupLocation({
        lat: placeDetails.location.lat,
        lng: placeDetails.location.lng,
      });
    }
  };

  const handlePickupLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setHasManualSearch(true);
    setPickupLocation({
      latitude: location.lat,
      longitude: location.lng,
      address: location.address,
    });
    setPickupSearchText(location.address);
    setTempPickupLocation({ lat: location.lat, lng: location.lng });
  };

  const confirmPickupLocation = async () => {
    if (!pickupLocation) {
      return;
    }

    // Check if location is within coverage area
    try {
      setBookingLoading(true);
      setError('');
      const response = await bookingsAPI.checkPickupLocation(
        pickupLocation.latitude,
        pickupLocation.longitude
      );

      if (!response.data.isWithinCoverage) {
        setError(response.data.message || 'Pickup location is outside our service coverage area. Please select a location within our coverage area.');
        setBookingLoading(false);
        return;
      }

      // Location is within coverage - proceed
      setHasManualSearch(true);
      setCurrentStep(STEPS.DROPOFF);
    } catch (error: any) {
      console.error('Error checking pickup location:', error);
      // If check fails, show error and don't proceed (fail closed for security)
      setError(error.response?.data?.message || 'Failed to verify pickup location. Please try again or select a different location.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleDropoffPlaceSelected = (value: string, placeDetails?: any) => {
    if (placeDetails?.location) {
      setTempDropoffLocation({
        lat: placeDetails.location.lat,
        lng: placeDetails.location.lng,
      });
    }
  };

  const handleDropoffLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setDropoffLocation({
      latitude: location.lat,
      longitude: location.lng,
      address: location.address,
    });
    setDropoffSearchText(location.address);
    setTempDropoffLocation({ lat: location.lat, lng: location.lng });
  };

  const handleCurrentLocationForPickup = (skipToDropoff = false) => {
    setHasManualSearch(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Reverse geocode to get address
          try {
            const geocoder = new google.maps.Geocoder();
            const result = await geocoder.geocode({ location: { lat, lng } });
            
            const address = result.results[0]?.formatted_address || 'Current Location';
            
            setPickupLocation({
              latitude: lat,
              longitude: lng,
              address: address,
            });
            setPickupSearchText(address);
            setTempPickupLocation({ lat, lng });
            
            if (skipToDropoff) {
              setCurrentStep(STEPS.DROPOFF);
            } else {
              setCurrentStep(STEPS.PICKUP);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            // Still proceed with coordinates
            setPickupLocation({
              latitude: lat,
              longitude: lng,
              address: 'Current Location',
            });
            setPickupSearchText('Current Location');
            setTempPickupLocation({ lat, lng });
            
            if (skipToDropoff) {
              setCurrentStep(STEPS.DROPOFF);
            } else {
              setCurrentStep(STEPS.PICKUP);
            }
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Could not get your current location. Please enable location permissions.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const [addingIntermediateStop, setAddingIntermediateStop] = useState(false);

  const confirmDropoffLocation = () => {
    if (dropoffLocation) {
      if (addingIntermediateStop) {
        // Add as intermediate stop
        const newStop = {
          latitude: dropoffLocation.latitude,
          longitude: dropoffLocation.longitude,
          address: dropoffLocation.address,
          order: intermediateDestinations.length + 1
        };
        setIntermediateDestinations([...intermediateDestinations, newStop]);
        setDropoffLocation(null);
        setDropoffSearchText('');
        setTempDropoffLocation(null);
        setAddingIntermediateStop(false);
        setCurrentStep(STEPS.DROPOFF); // Allow adding more stops or final destination
      } else {
        setCurrentStep(STEPS.BOOKING_DETAILS);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that booking date/time is in the future
    if (startDateTime) {
      const bookingDateTime = new Date(startDateTime);
      const now = new Date();
      if (bookingDateTime <= now) {
        setError('Please select a date and time in the future. Past bookings are not allowed.');
        alert('Please select a date and time in the future. Past bookings are not allowed.');
        return;
      }
    }
    
    if (!pickupLocation || !dropoffLocation) {
      setError('Both pickup and dropoff locations are required');
      return;
    }

    if (!startDateTime || !endDateTime) {
      setError('Start and end date/time are required');
      return;
    }

    // Validate that booking date/time is in the future
    if (startDateTime) {
      const bookingDateTime = new Date(startDateTime);
      const now = new Date();
      if (bookingDateTime <= now) {
        setError('Please select a date and time in the future. Past bookings are not allowed.');
        alert('Please select a date and time in the future. Past bookings are not allowed.');
        return;
      }
    }

    // Validate end datetime is not before minimum
    if (minEndDateTime && endDateTime < minEndDateTime) {
      setError(`End datetime cannot be earlier than the estimated minimum time. Please set a later time.`);
      return;
    }

    setBookingLoading(true);
    setError('');

    try {
      // Extract service date and timeSlot from datetime inputs
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      const serviceDateStr = startDate.toISOString().split('T')[0];
      const startTimeStr = startDateTime.split('T')[1];
      const endTimeStr = endDateTime.split('T')[1];
      const timeSlot = `${startTimeStr}-${endTimeStr}`;
      
      await bookingsAPI.createBooking({
        providerId: provider!._id || provider!.id!,
        bookingDate: serviceDateStr, // Service date (date when the trip should happen)
        timeSlot,
        notes,
        serviceType: 'driver',
        pickupLocation,
        dropoffLocation,
        numberOfNights,
        intermediateDestinations: intermediateDestinations.length > 0 ? intermediateDestinations : undefined,
        tripType: tripType, // Use selected trip type
      });
      
      navigate('/bookings');
    } catch (err: any) {
      console.error('Booking error:', err);
      setError(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-black" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Driver Not Found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.PICKUP:
        return (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <CombinedLocationSelector
              value={pickupSearchText}
              onChange={handlePickupPlaceSelected}
              onCurrentLocationClick={() => handleCurrentLocationForPickup(false)}
              placeholder="Search pickup location (e.g., Connaught Place, Delhi)"
              initialLocation={tempPickupLocation}
              onLocationSelect={handlePickupLocationSelect}
              onConfirm={confirmPickupLocation}
              confirmButtonText="Confirm Pickup"
              stepTitle="Step 1: Select Pickup Location"
              address={pickupLocation?.address}
              middleContent={
                !hasManualSearch && recentLocations.length > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Recent Trips - Click to use
                    </h5>
                    <div className="space-y-2">
                      {recentLocations.map((location, index) => (
                        <button
                          key={index}
                          onClick={() => selectRecentLocation(location)}
                          className="w-full text-left p-3 bg-white rounded-md hover:bg-white transition-colors border border-gray-700"
                        >
                          <div className="flex items-start text-xs">
                            <MapPin className="h-3 w-3 text-green-600 mr-1 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 line-clamp-1">{location.pickup.address}</span>
                          </div>
                          {location.intermediateDestinations && Array.isArray(location.intermediateDestinations) && location.intermediateDestinations.length > 0 && (
                            <>
                              {location.intermediateDestinations
                                .sort((a: { order: number }, b: { order: number }) => (a.order || 0) - (b.order || 0))
                                .map((stop: { order: number; address: string }, stopIndex: number) => (
                                  <div key={`stop-${stopIndex}-${stop.order || stopIndex}`} className="flex items-start text-xs mt-1">
                                    <MapPin className="h-3 w-3 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700 line-clamp-1">Stop {stop.order || stopIndex + 1}: {stop.address}</span>
                                  </div>
                                ))}
                            </>
                          )}
                          <div className="flex items-start text-xs mt-1">
                            <MapPin className="h-3 w-3 text-red-600 mr-1 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 line-clamp-1">{location.dropoff.address}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      Clicking a recent trip will auto-fill locations and skip to booking details
                    </p>
                  </div>
                ) : !hasManualSearch ? (
                  <div className="bg-white border border-gray-800 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-300 flex items-center">
                      <Clock className="h-3 w-3 mr-2 flex-shrink-0" />
                      💡 Tip: After your first booking, recent trips will appear here for quick rebooking!
                    </p>
                  </div>
                ) : null
              }
            />
          </div>
        );

      case STEPS.DROPOFF:
        return (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <p className="text-sm text-green-800">
                ✓ Pickup location confirmed: {pickupLocation?.address}
              </p>
            </div>

            {/* Show dropoff confirmation if already set, but still allow editing */}
            {dropoffLocation && !addingIntermediateStop && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                <p className="text-sm text-green-800">
                  ✓ Dropoff location confirmed: {dropoffLocation.address}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  You can edit the dropoff location below if needed
                </p>
              </div>
            )}

            <CombinedLocationSelector
              value={dropoffSearchText || dropoffLocation?.address || ''}
              onChange={handleDropoffPlaceSelected}
              placeholder={addingIntermediateStop ? "Search intermediate stop location" : "Search dropoff location (e.g., India Gate, Delhi)"}
              initialLocation={tempDropoffLocation || (dropoffLocation ? { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude } : null)}
              onLocationSelect={handleDropoffLocationSelect}
              onConfirm={confirmDropoffLocation}
              confirmButtonText={addingIntermediateStop ? `Add Stop ${intermediateDestinations.length + 1}` : (dropoffLocation ? 'Update Dropoff Location' : 'Confirm Dropoff')}
              stepTitle={addingIntermediateStop ? `Step 2: Add Intermediate Stop ${intermediateDestinations.length + 1}` : "Step 2: Select Dropoff Location"}
              address={dropoffLocation?.address}
            />

            {addingIntermediateStop && (
              <button
                type="button"
                onClick={() => {
                  setAddingIntermediateStop(false);
                  setCurrentStep(STEPS.BOOKING_DETAILS);
                }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Done Adding Stops
              </button>
            )}
          </div>
        );

      case STEPS.BOOKING_DETAILS:
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-800 font-semibold mb-2">✓ Locations Confirmed</p>
              <p className="text-xs text-green-700">Pickup: {pickupLocation?.address}</p>
              <p className="text-xs text-green-700">Dropoff: {dropoffLocation?.address}</p>
              {distance && (
                <p className="text-xs text-green-700 font-semibold mt-1">
                  Distance: {distance.toFixed(2)} km
                </p>
              )}
            </div>

            <div className="bg-white border border-gray-300 rounded-md p-4">
              <h3 className="font-semibold text-black mb-2">Step 3: Booking Details</h3>
              <p className="text-sm text-gray-800">
                Fill in your booking details to complete the reservation.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Number of Nights */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Moon className="inline h-4 w-4 mr-1" />
                  Number of Nights (Auto-calculated)
                </label>
                <input
                  type="number"
                  min="0"
                  value={numberOfNights}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setNumberOfNights(isNaN(value) ? 0 : value);
                  }}
                  className="w-full px-4 py-2 border border-gray-700 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-black bg-white"
                />
                <p className="text-xs text-gray-500 mt-1">Automatically calculated from start and end dates. You can manually adjust if needed.</p>
              </div>

              {/* Trip Type Selection - Show when distance is available */}
              {distance !== null && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Trip Type:
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="roundTrip"
                        checked={tripType === 'roundTrip'}
                        onChange={async () => {
                          setTripType('roundTrip');
                          // Pass the new trip type directly to avoid state update timing issues
                          await calculatePrice('roundTrip');
                        }}
                        className="h-4 w-4 text-black focus:ring-gray-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Round Trip</span>
                    </label>
                    <label className={`flex items-center space-x-2 ${oneWayAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                      <input
                        type="radio"
                        name="tripType"
                        value="oneWay"
                        checked={tripType === 'oneWay'}
                        onChange={async () => {
                          if (oneWayAllowed) {
                            setTripType('oneWay');
                            // Pass the new trip type directly to avoid state update timing issues
                            await calculatePrice('oneWay');
                          }
                        }}
                        disabled={!oneWayAllowed}
                        className="h-4 w-4 text-black focus:ring-gray-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">One Way</span>
                    </label>
                  </div>
                  {!oneWayAllowed && (
                    <p className="text-xs text-yellow-600 mt-2">
                      ⚠️ One-way trips are only available for distances less than the threshold.
                    </p>
                  )}
                  {oneWayAllowed && (
                    <p className="text-xs text-gray-500 mt-2">
                      {tripType === 'roundTrip' 
                        ? `Round trip includes return journey (Total: ${distance?.toFixed(2) ?? '0.00'} km)`
                        : `One-way trip (Distance: ${oneWayDistance?.toFixed(2) ?? '0.00'} km)`}
                    </p>
                  )}
                </div>
              )}

              {/* Start DateTime */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={startDateTime ?? ''}
                  onChange={(e) => {
                    setStartDateTime(e.target.value);
                    // Extract date for availability checks
                    if (e.target.value) {
                      const dateOnly = e.target.value.split('T')[0];
                      setBookingDate(dateOnly);
                      const timeOnly = e.target.value.split('T')[1];
                      setStartTime(timeOnly);
                    }
                  }}
                  min={new Date().toISOString().slice(0, 16)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-black"
                />
              </div>

              {/* Driver Availability Display */}
              {bookingDate && !hasSchedule && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    Driver Not Available
                  </h4>
                  <p className="text-sm text-red-800">
                    The driver hasn't set their availability for {new Date(bookingDate).toLocaleDateString()}.
                    They may not be working on this date. Please try a different date or contact the driver.
                  </p>
                </div>
              )}

              {bookingDate && hasSchedule && !isAvailable && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    Driver Not Available
                  </h4>
                  <p className="text-sm text-red-800">
                    The driver has marked themselves as unavailable on {new Date(bookingDate).toLocaleDateString()}.
                    Please choose a different date.
                  </p>
                </div>
              )}

              {bookingDate && hasSchedule && isAvailable && bookedTimes.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                    Driver Partially Available
                  </h4>
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">⛔ Already Booked:</p>
                    <div className="space-y-1">
                      {bookedTimes.map((time, index) => (
                        <div key={index} className="flex items-center text-sm text-red-700 bg-red-50 rounded px-2 py-1">
                          <span className="font-medium">🚫</span>
                          <span className="ml-2">{time.startTime} - {time.endTime}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">✅ Still Available:</p>
                    <div className="space-y-1">
                      {calculateAvailableRanges(bookedTimes).map((time, index) => (
                        <div key={index} className="flex items-center text-sm text-green-700 bg-green-50 rounded px-2 py-1">
                          <span className="font-medium">✓</span>
                          <span className="ml-2">{time.startTime} - {time.endTime}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">
                    ℹ️ Please choose a time that falls within the available ranges above.
                  </p>
                </div>
              )}

              {bookingDate && hasSchedule && isAvailable && bookedTimes.length === 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Driver Fully Available
                  </h4>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">✅ Available All Day:</p>
                    <div className="space-y-1">
                      {calculateAvailableRanges([]).map((time, index) => (
                        <div key={index} className="flex items-center text-sm text-green-700 bg-green-100 rounded px-2 py-1">
                          <span className="font-medium">✓</span>
                          <span className="ml-2">{time.startTime} - {time.endTime}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    🎉 You can book any time on {new Date(bookingDate).toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* End DateTime */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date & Time * 
                  {minEndDateTime && (
                    <span className="text-xs text-green-600 ml-2">
                      (Minimum: {new Date(minEndDateTime).toLocaleString()})
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={endDateTime ?? ''}
                    onChange={(e) => {
                      setEndDateTime(e.target.value);
                      // Extract time for timeSlot format
                      if (e.target.value) {
                        const timeOnly = e.target.value.split('T')[1];
                        setEndTime(timeOnly);
                      }
                    }}
                    min={minEndDateTime || startDateTime || new Date().toISOString().slice(0, 16)}
                    required
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-black"
                  />
                  {minEndDateTime && endDateTime && endDateTime < minEndDateTime && (
                    <button
                      type="button"
                      onClick={() => setEndDateTime(minEndDateTime)}
                      className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded-md hover:bg-green-200 transition-colors whitespace-nowrap"
                      title="Reset to minimum datetime"
                    >
                      Reset to Min
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ⏱️ Minimum time calculated: Travel time + 30 min buffer. You can set a LATER time if needed, but not earlier.
                </p>
                {endDateTime && minEndDateTime && endDateTime < minEndDateTime && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ End datetime cannot be earlier than the estimated minimum time.
                  </p>
                )}
              </div>

              {/* Multi-Destination Stops */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Intermediate Stops (Optional)
                  <span className="text-xs text-gray-500 ml-2">
                    ({intermediateDestinations.length}/{maxDestinations})
                  </span>
                </label>
                <div className="space-y-2 mb-2">
                  {intermediateDestinations.map((stop, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-2 rounded-md">
                      <span className="text-xs font-semibold text-gray-300 w-6">{index + 1}.</span>
                      <span className="flex-1 text-sm text-gray-700">{stop.address}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newStops = intermediateDestinations.filter((_, i) => i !== index);
                          setIntermediateDestinations(newStops.map((s, i) => ({ ...s, order: i + 1 })));
                        }}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                {intermediateDestinations.length < maxDestinations && (
                  <button
                    type="button"
                    onClick={() => {
                      // Add a new stop - user will search for it
                      setAddingIntermediateStop(true);
                      setCurrentStep(STEPS.DROPOFF); // Reuse dropoff search for intermediate stops
                    }}
                    className="w-full px-4 py-2 border-2 border-dashed border-gray-700 rounded-md text-gray-300 hover:border-black hover:text-black400 transition-colors"
                  >
                    + Add Stop
                  </button>
                )}
                {intermediateDestinations.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Route: Pickup → {intermediateDestinations.map(s => s.address).join(' → ')} → Dropoff
                    {tripType === 'roundTrip' && ' → Pickup'}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any special requirements or instructions..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-black"
                />
              </div>

              {/* Price Estimate */}
              {estimatedPrice && (
                <div className="bg-white border border-gray-300 rounded-md p-4">
                  <h4 className="font-semibold text-black mb-3">Estimated Price</h4>
                  
                  {/* Distance & Slab Info */}
                  <div className="bg-white rounded-md p-2 mb-3">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-300">Distance (via Google Maps):</span>
                      <span className="font-semibold text-black">{distance?.toFixed(2)} km</span>
                    </div>
                    {estimatedPrice.slabUsed && (
                      <div className="flex justify-between items-center text-xs mt-1">
                        <span className="text-gray-300">Pricing Slab:</span>
                        <span className="font-semibold text-green-600">{estimatedPrice.slabUsed}</span>
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-1 text-sm">
                    {distance !== null && (
                      <div className="flex justify-between text-xs text-gray-300 mb-2 pb-2 border-b border-gray-700">
                        <span>Total Distance:</span>
                        <span className="font-semibold">{distance.toFixed(2)} km</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Base Fare:</span>
                      <span>₹{estimatedPrice.baseFare?.toFixed(2) || estimatedPrice.distanceCost?.toFixed(2)}</span>
                    </div>
                    {numberOfNights > 0 && estimatedPrice.nightlyCost !== undefined && estimatedPrice.nightlyCost !== null && estimatedPrice.nightlyCost > 0 && (
                      <div className="flex justify-between">
                        <span>Overnight Stay ({numberOfNights} nights):</span>
                        <span>₹{estimatedPrice.nightlyCost.toFixed(2)}</span>
                      </div>
                    )}
                    {estimatedPrice.overtimeCharge !== undefined && estimatedPrice.overtimeCharge !== null && estimatedPrice.overtimeCharge > 0 && (
                      <div className="flex justify-between text-xs text-gray-300">
                        <span>Overtime Charge:</span>
                        <span>₹{estimatedPrice.overtimeCharge.toFixed(2)}</span>
                      </div>
                    )}
                    {estimatedPrice.nightDriveCharge !== undefined && estimatedPrice.nightDriveCharge !== null && estimatedPrice.nightDriveCharge > 0 && (
                      <div className="flex justify-between text-xs text-gray-300">
                        <span>Night Drive Charge:</span>
                        <span>₹{estimatedPrice.nightDriveCharge.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium pt-2 border-t">
                      <span>Subtotal:</span>
                      <span>₹{estimatedPrice.subtotal.toFixed(2)}</span>
                    </div>
                    {estimatedPrice.platformFee !== undefined && estimatedPrice.platformFee !== null && estimatedPrice.platformFee > 0 && (
                      <div className="flex justify-between text-xs text-gray-300">
                        <span>Platform Fee{adminSettings?.driverPricing?.platformFeePercentage ? ` (${adminSettings.driverPricing.platformFeePercentage}%)` : ''}:</span>
                        <span>₹{estimatedPrice.platformFee.toFixed(2)}</span>
                      </div>
                    )}
                    {estimatedPrice.tax !== undefined && estimatedPrice.tax !== null && estimatedPrice.tax > 0 && (
                      <div className="flex justify-between text-xs text-gray-300">
                        <span>Tax{adminSettings?.driverPricing?.taxPercentage ? ` (GST ${adminSettings.driverPricing.taxPercentage}%)` : ''}:</span>
                        <span>₹{estimatedPrice.tax.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t-2 border-gray-300 text-black">
                      <span>Total Amount:</span>
                      <span className="text-black">₹{estimatedPrice.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Terms and Conditions Agreement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTnC}
                    onChange={(e) => setAgreedToTnC(e.target.checked)}
                    className="mt-1 h-4 w-4 text-black focus:ring-gray-500 border-gray-300 rounded"
                  />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/terms?userType=customer" target="_blank" className="text-black hover:text-gray-800 underline font-medium">
                    Terms and Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy?userType=customer" target="_blank" className="text-black hover:text-gray-800 underline font-medium">
                    Privacy Policy
                  </a>
                  {' '}(Customer)
                </span>
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentStep(STEPS.DROPOFF)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  <ChevronLeft className="mr-2 h-5 w-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={bookingLoading || !provider.isAvailable || !agreedToTnC}
                  className="flex-1 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                  title={!agreedToTnC ? 'Please agree to Terms and Conditions' : ''}
                >
                  {bookingLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Booking...
                    </span>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              </div>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <button
          onClick={() => {
            if (currentStep > STEPS.PICKUP) {
              // Go to previous step
              if (currentStep === STEPS.PICKUP) {
                setCurrentStep(STEPS.PICKUP);
              } else if (currentStep === STEPS.DROPOFF) {
                setCurrentStep(STEPS.PICKUP);
              } else if (currentStep === STEPS.DROPOFF) {
                setCurrentStep(STEPS.DROPOFF);
              } else if (currentStep === STEPS.BOOKING_DETAILS) {
                setCurrentStep(STEPS.DROPOFF);
              }
            } else {
              navigate('/');
            }
          }}
            className="flex items-center text-gray-300 hover:text-white mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {currentStep > STEPS.PICKUP ? 'Back' : 'Back to Home'}
        </button>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Driver Info Card */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <div className="text-center mb-4">
                {provider.avatar ? (
                  <img
                    src={provider.avatar}
                    alt={provider.name}
                    className="h-24 w-24 rounded-full object-cover mx-auto mb-4"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <span className="text-black font-bold text-3xl">
                      {provider.name.charAt(0)}
                    </span>
                  </div>
                )}
                <h2 className="text-xl font-bold text-white">{provider.name}</h2>
                <p className="text-gray-300 text-sm">⭐ {provider.rating.toFixed(1)} ({provider.reviewCount} reviews)</p>
              </div>

              {provider.bio && (
                <p className="text-gray-300 text-sm mb-4 pb-4 border-b border-gray-700">
                  {provider.bio}
                </p>
              )}

              <div className="space-y-2 text-sm">
                {provider.experience && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Experience:</span>
                    <span className="font-semibold">{provider.experience} years</span>
                  </div>
                )}
                {provider.hourlyRate && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Base Rate:</span>
                    <span className="font-semibold">₹{typeof provider.hourlyRate === 'number' ? provider.hourlyRate.toFixed(2) : provider.hourlyRate}/hr</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-gray-300">Status:</span>
                  <span className={`font-semibold ${provider.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                    {provider.isAvailable ? '✓ Available' : '✗ Busy'}
                  </span>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="mt-6 pt-6 border-t">
                <p className="text-xs text-gray-500 font-semibold mb-2">BOOKING PROGRESS</p>
                <div className="space-y-2">
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.PICKUP ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep > STEPS.PICKUP ? '✓' : currentStep >= STEPS.PICKUP ? '→' : '○'} Pickup Location
                  </div>
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.DROPOFF ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep > STEPS.DROPOFF ? '✓' : currentStep >= STEPS.DROPOFF ? '→' : '○'} Dropoff Location
                  </div>
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.BOOKING_DETAILS ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep >= STEPS.BOOKING_DETAILS ? '→' : '○'} Booking Details
                  </div>
                </div>
              </div>
            </div>
            
            {/* Reviews Section */}
            {provider && (provider._id || provider.id) && (
              <div className="mt-6">
                <ReviewDisplay driverId={provider._id || provider.id!} />
              </div>
            )}
          </div>

          {/* Booking Form */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-2xl font-bold text-white mb-6">Book Driver Service</h3>
              {renderStepContent()}
            </div>
            
            {/* Reviews Section */}
            {provider && (provider._id || provider.id) && (
              <div className="mt-6">
                <ReviewDisplay driverId={provider._id || provider.id!} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDriverPage;
